import { Tooltip } from '@mui/material'
import { addMinutes, differenceInMinutes, format, max, min, subMinutes } from 'date-fns'
import React, { FC, Fragment, useEffect, useReducer, useState } from 'react'
import { DraggableData, Rnd } from 'react-rnd'
import styled from 'styled-components'
import { mutate } from 'swr'
import { ScheduleType } from '../../../common/types/calendar'
import { Color } from '../../../common/types/color'
import { roundMinutes } from '../../../common/utils/date'
import { ERROR_DUPLICATE } from '../../../constants/calendar/error'
import { getCalendarRange } from '../../../constants/calendar/getCalendarRange'
import { rndProps } from '../../../constants/calendar/rnd'
import { useCalendarContext } from '../../../context/calendar'
import useWindowWidth from '../../../hooks/useWindowSize'
import { setSchedule } from '../../../options/setSchedule'
import { useModal } from 'react-hooks-use-modal'
import { useForm, SubmitHandler } from "react-hook-form"
import axios from 'axios'
import { useAuth } from 'hooks/use-auth'
import IdSearchButton from 'components/IdSearchButton'
import SubmitPerformances from 'components/SubmitPerformances'
import config from 'config'
import { Time, makeUrlParams } from 'utils';
import { removeKeyWithValue } from 'utils'
import { Wrapper, PageTitle, CreateNew, ModalStyle, FormLabel, FormSelect, FormInput, FormLabelInputWrapper, ModalTitle, ModalForm, ModalSubmit, LoadingContainer, ModalClose, DivMargin } from 'styles/styles';
import { CircularProgress } from '@mui/material';

const getCellByDatetime = (roundedDate: Date, column_id: string) => {
  const selector =
    `div[data-date="${format(
      roundedDate,
      'yyyy-MM-dd'
    )}"][data-hours="${roundedDate.getHours()}"][data-minutes="${roundedDate.getMinutes()}"]` +
    (column_id ? `[data-column-id="${column_id}"]` : '')
  return document.querySelector(selector)
}

function isOverlappedRect(data: DraggableData) {
  const rect = data.node.getBoundingClientRect()
  const top = rect.top + data.deltaY
  const bottom = rect.bottom + data.deltaY

  for (let y = top; y <= bottom; y += 6) {
    const point = [(rect.left + rect.right) / 2, y === bottom ? y - 1 : y]
    const overlappedElements = document.elementsFromPoint(point[0], point[1]) as HTMLElement[]
    const overlappedSchedules = overlappedElements.filter(
      (element) => element.dataset.isSchedule === 'true' && element !== data.node
    )
    if (overlappedSchedules[0]) {
      return true
    }
  }
  return false
}

const applyStyleByEventType = (
  data: DraggableData & { direction?: string },
  eventType: 'onDrag' | 'onDragStart' | 'onDragStop' | 'onResize' | 'onResizeStart' | 'onResizeStop'
) => {
  const { node } = data
  const child = node.children[0] as HTMLElement
  if (eventType === 'onDrag') {
    if (isOverlappedRect(data)) {
      child.style.cursor = 'not-allowed'
      node.style.backgroundColor = '#ff0000'
      // child.style.color = '#ffffff'
      node.style.opacity = '0.9'
    } else {
      child.style.cursor = 'pointer'
      node.style.backgroundColor = 'initial'
      // child.style.color = 'initial'
      node.style.opacity = '0.5'
    }
  } else if (eventType === 'onDragStart') {
    document.body.style.userSelect = 'none'
    node.style.opacity = '0.5'
    node.style.zIndex = '1'
  } else if (eventType === 'onDragStop') {
    document.body.style.userSelect = 'initial'
    node.style.opacity = 'initial'
    node.style.backgroundColor = 'initial'
    node.style.zIndex = '0'
    child.style.cursor = 'pointer'
  } else if (eventType === 'onResize') {
    const resizeHandles = data.node.querySelectorAll('.resize-handle') as NodeListOf<HTMLElement>

    if (isOverlappedRect(data)) {
      resizeHandles[data.direction === 'top' ? 0 : 1].style.cursor = 'not-allowed'
      node.style.backgroundColor = '#ff0000'
      node.style.opacity = '0.9'
    } else {
      resizeHandles[data.direction === 'top' ? 0 : 1].style.cursor = 'row-resize'
      data.node.style.backgroundColor = 'initial'
      node.style.opacity = '0.5'
    }
  } else if (eventType === 'onResizeStart') {
    document.body.style.userSelect = 'none'
    node.style.opacity = '0.5'
  } else if (eventType === 'onResizeStop') {
    document.body.style.userSelect = 'initial'
    node.style.opacity = 'initial'
    node.style.backgroundColor = 'initial'
    const resizeHandles = node.querySelectorAll<HTMLElement>('.resize-handle')
    resizeHandles.forEach((resizeHandle) => (resizeHandle.style.cursor = 'row-resize'))
  }
}

const Schedule: FC<ScheduleType & { minutes: number }> = (props) => {
  const windowWidth = useWindowWidth()
  /**
   * ResizeStop時にstateを更新しない場合バグがあるため撮り急ぎの対応
   * 参照: {@link https://github.com/bokuweb/react-rnd/issues/900#issuecomment-1371675695}
   */
  const [key, updateKey] = useReducer((x) => x + 1, 0)
  const [isLoading, setIsLoading] = useState(false)
  const [isDrag, setIsDrag] = useState(false)
  const { date, filtered } = useCalendarContext()
  const { color, column_id, end_date, id, minutes, name, start_date } = props
  const calendarRange = getCalendarRange(date)
  const roundedStartDate = roundMinutes(max([calendarRange.start, start_date]), minutes)
  const roundedEndDate = roundMinutes(min([calendarRange.end, end_date]), minutes)
  const startCell = getCellByDatetime(roundedStartDate, column_id) as HTMLElement | null
  const endCell = getCellByDatetime(roundedEndDate, column_id) as HTMLElement | null
  const [selectedCalendarData, setSelectedCalendarData] = useState<any>([])

  const width = startCell ? startCell.parentElement?.clientWidth || 0 : 0
  const [rndState, setRndState] = useState({
    position: {
      x: width * filtered.findIndex((filter) => filter.id === column_id),
      y: differenceInMinutes(roundedStartDate, calendarRange.start) * (5 / 15) + 2, // ズレの解消
    },
    size: {
      height: differenceInMinutes(roundedEndDate, roundedStartDate) * (5 / 15),
      width,
    },
  })


  interface ParentDataProps {
    records_process_id: number
    records_product_number_id: number
    process_id: number
    process_display_id: string
    process_name: string
    equipment_id: number
    equipment_display_id: string
    equipment_name: string
    charged_user_id: number
    charged_user_display_id: string
    charged_user_name: string
    volume: string
    setup_time: string
    tact_time: string
    start_date: string
    end_date: string
    estimated_time: number
    process_deadline: string
    memo: string
    [key: string]: string | number | boolean;
  }

  const auth = useAuth();
  const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(false);
  //modal
  const [Modal, open, close, isOpen] = useModal('root', { preventScroll: true, });
  const { reset, register, handleSubmit, setValue, watch, formState: { errors } } = useForm<ParentDataProps>();
  const onSubmit: SubmitHandler<ParentDataProps> = async (submitData: ParentDataProps) => {
    let alertList: any[] = [];
    const requiredFields = parentArray.filter(obj => obj.required === true).map(obj => obj.field);
    requiredFields.forEach((key) => {
      if (submitData[key] === undefined) {
        alertList.push(key)
      }
    });
    if (alertList.length) {
      let text = '';
      parentArray.forEach(item => {
        if (alertList.includes(item.field)) {
          text += `${item.title}を埋めてください。\n`;
        }
      });
      if (text !== '') {
        alert(text);
        throw new Error('必須項目が未入力です');
      }
    }
    if (submitData.start_date > submitData.end_date) {
      window.alert("開始時刻は終了時刻より前にしてください");
      throw new Error('開始時刻は終了時刻より前にしてください');
    }
    /* submitDataで日付の部分がnullだと、仕様で文字列になってしまうため、対応 */
    if (submitData.start_date === "NaN-aN-aNTaN:aN") {
      submitData.start_date = "";
    }
    if (submitData.end_date === "NaN-aN-aNTaN:aN") {
      submitData.end_date = "";
    }
    /* ここまで */

    submitData = removeKeyWithValue(submitData, '');
    const branchData = {
      is_ended: submitData.is_ended,
      process_id: submitData.process_id,
      process_display_id: submitData.process_display_id,
      process_name: submitData.process_name,
      equipment_id: submitData.equipment_id,
      equipment_display_id: submitData.equipment_display_id,
      equipment_name: submitData.equipment_name,
      charged_user_id: submitData.charged_user_id,
      charged_user_display_id: submitData.charged_user_display_id,
      charged_user_name: submitData.charged_user_name,
      volume: submitData.volume,
      setup_time: submitData.setup_time,
      tact_time: submitData.tact_time,
      start_date: submitData.start_date ? new Date(submitData.start_date).toISOString() : undefined,
      end_date: submitData.end_date ? new Date(submitData.end_date).toISOString() : undefined,
      estimated_time: submitData.estimated_time,
      process_deadline: submitData.process_deadline,
      memo: submitData.memo,
    }
    try {
      setIsSubmitDisable(true);
      await axios.patch(`/v0/records-product-numbers/${submitData.records_product_number_id}/records-processes`, branchData, {
        headers: auth.authorizationHeader,
        params: {
          selected_branch: submitData.records_process_id,
          factory_id: auth.factoryId
        }
      })
      window.alert("変更が完了しました");
      mutate(['/api/schedules', date, filtered])
      close();
    } catch (error) {
      window.alert("エラーが発生し、変更に失敗しました");
    }
    setIsSubmitDisable(false);
  };
  //modal end


  //arrayの生成

  const parentArray = [
    { title: '', field: 'records_product_number_id' },
    { title: '', field: 'records_process_id' },
    { title: '作業区分', field: 'status', disable: true },
    { title: '終了フラグ', field: 'is_ended', type: 'checkbox' },
    { title: '', field: 'process_id' },
    { title: '工程ID', field: 'process_display_id', type: 'alphanumeric', required: true, disable: true },
    { title: '工程名称', field: 'process_name', search: 'processes', required: true, disable: true },
    { title: '', field: 'equipment_id' },
    { title: '設備ID', field: 'equipment_display_id', disable: true },
    { title: '設備名称', field: 'equipment_name', search: 'equipments', disable: true },
    { title: '', field: 'charged_user_id' },
    { title: '担当者ID', field: 'charged_user_display_id', disable: true, required: true },
    { title: '担当者名', field: 'charged_user_name', disable: true, required: true, search: "users" },
    { title: '製作数量', field: 'volume', default: 0, type: 'number' },
    { title: '段取時間(分)', field: 'setup_time', type: 'number' },
    { title: 'タクトタイム(分)', field: 'tact_time', type: 'number' },
    { title: '工程開始時刻', field: 'start_date', type: 'datetime-local' },
    { title: '工程終了時刻', field: 'end_date', type: 'datetime-local' },
    { title: '計画加工時間(分)', field: 'estimated_time', type: 'number', required: true },
    { title: '工程納期', field: 'process_deadline', default: Time.format(new Date(), "YYYY-MM-DD"), type: 'date' },
    { title: '共有したか否か', field: 'is_outsourcing', type: 'checkbox', disable: true },
    { title: 'メモ', field: 'memo', default: 'メモ', type: 'text' }
  ]
  const PAGE_TITLE = "登録済み工程一覧"
  const columnsArray: any = [];
  for (const element of parentArray) {
    if (element.title) {
      columnsArray.push({
        title: element.title,
        field: element.field,
        cellStyle: {
          minWidth: 150,
          maxWidth: 150,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      })
    }
  }

  const [selectedProcessContents, setSelectedProcessContents] = useState<any>('');


  useEffect(() => {
    setValue('process_id', selectedProcessContents.process_id);
    setValue('process_display_id', selectedProcessContents.display_id);
    setValue('process_name', selectedProcessContents.name);
  }, [selectedProcessContents])

  const [selectedEquipmentContents, setSelectedEquipmentContents] = useState<any>('');

  useEffect(() => {
    setValue('equipment_id', selectedEquipmentContents.equipment_id);
    setValue('equipment_display_id', selectedEquipmentContents.display_id);
    setValue('equipment_name', selectedEquipmentContents.name);
  }, [selectedEquipmentContents])

  const [selectedUserContents, setSelectedUserContents] = useState<any>('');

  useEffect(() => {
    setValue('charged_user_id', selectedUserContents.user_id);
    setValue('charged_user_display_id', selectedUserContents.display_id);
    setValue('charged_user_name', selectedUserContents.name);
  }, [selectedUserContents])

  const listItems = parentArray.map((element: any, index) => {
    if (element.title) {
      return (
        <Fragment key={index}>
          <FormLabelInputWrapper>
            <FormLabel htmlFor={element.field}>{element.title}</FormLabel>
            <FormInput {...register(element.field)}
              {...(element.type && { type: config.inputErrorMessage[element.type].type })}
              {...(element.type && { placeholder: config.inputErrorMessage[element.type].placeholder })}
              {...(element.type && { pattern: config.inputErrorMessage[element.type].pattern })}
              {...(element.type && { title: config.inputErrorMessage[element.type].title })}
              {...(element.required && { required: true })}
              {...(element.disable && { disabled: true })}
              {...(element.type && { maxLength: config.inputErrorMessage[element.type].maxLength })}
              {...(element.type === "number" && { max: config.inputErrorMessage[element.type].max })}
              {...(element.field.includes("branch") && { max: 99 })}
            />
            {element.search === "equipments" ? <IdSearchButton search={element.search} setSelectedContents={setSelectedEquipmentContents} /> :
              element.search === "processes" ? <IdSearchButton search={element.search} setSelectedContents={setSelectedProcessContents} /> :
                element.search === "users" ? <IdSearchButton search={element.search} setSelectedContents={setSelectedUserContents} /> : <></>
            }
          </FormLabelInputWrapper>
        </Fragment>
      )
    }
  });


  /* 計画加工時間(分)を自動入力 */
  const volume: string = watch('volume');
  const setup_time: string = watch('setup_time');
  const tact_time: string = watch('tact_time');
  useEffect(() => {
    if (volume && setup_time && tact_time) {
      setValue('estimated_time', parseInt(setup_time) + parseInt(tact_time) * parseInt(volume));
    }
  }, [volume, setup_time, tact_time]);

  useEffect(() => {
    setRndState({
      position: {
        x: width * filtered.findIndex((filter) => filter.id === column_id),
        y: differenceInMinutes(roundedStartDate, calendarRange.start) * (5 / 15) + 2, // ズレの解消
      },
      size: {
        height: differenceInMinutes(roundedEndDate, roundedStartDate) * (5 / 15),
        width,
      },
    })
  }, [calendarRange.start.getTime(), calendarRange.end.getTime(), column_id])

  useEffect(() => {
    setRndState((prev) => ({
      ...prev,
      ...{ size: { height: prev.size.height, width } },
      position: {
        x: width * filtered.findIndex((filter) => filter.id === column_id),
        y: prev.position.y,
      },
    }))
  }, [windowWidth, width, filtered, column_id])

  useEffect(() => {
    setRndState({
      position: {
        x: width * filtered.findIndex((filter) => filter.id === column_id),
        y: differenceInMinutes(roundedStartDate, calendarRange.start) * (5 / 15) + 2, // ズレの解消
      },
      size: {
        height: differenceInMinutes(roundedEndDate, roundedStartDate) * (5 / 15),
        width,
      },
    })
    setIsLoading(false)
  }, [start_date.getTime(), end_date.getTime(), column_id])

  async function handleScheduleMouseUp(id: string) {
    if (isDrag) {
      return
    }
    const result: any = await axios.get(`/v0/factories/${auth.factoryId}/records-processes`, {
      headers: auth.authorizationHeader,
      params: { id: id }
    });
    result.data[0].end_date = Time.utcToHtmlDatetimeLocal(result.data[0].end_date);
    result.data[0].start_date = Time.utcToHtmlDatetimeLocal(result.data[0].start_date);
    result.data[0].is_outsourcing = parseInt(result.data[0].is_outsourcing);
    setSelectedCalendarData(result.data[0])
    reset(result.data[0]);
    open();
  }

  if (!startCell || !endCell) {
    return <></>
  }

  return (
    <ScheduleWrapper isLoading={isLoading}>
      <Rnd
        {...rndProps}
        key={key}
        data-is-schedule={true}
        size={rndState.size}
        position={rndState.position}
        resizeGrid={[width, 5]}
        dragGrid={[width, 5]}
        minHeight={5}
        disableDragging={isLoading}
        onDrag={(_, data) => applyStyleByEventType(data, 'onDrag')}
        onDragStart={(_, data) => applyStyleByEventType(data, 'onDragStart')}
        onDragStop={(_, data) => {
          applyStyleByEventType(data, 'onDragStop')
          const sumDeltaY = data.y - data.deltaY - rndState.position.y
          const cellCount = Math.floor(sumDeltaY / 5)
          const changeMinutes = cellCount * 15

          if (changeMinutes === 0) {
            return
          }

          if (isOverlappedRect(data)) {
            alert(ERROR_DUPLICATE)
          } else {
            setRndState({ ...rndState, position: { x: data.x, y: data.y } })
            setSchedule(
              id,
              addMinutes(start_date, changeMinutes),
              addMinutes(end_date, changeMinutes),
              column_id,
              auth.factoryId,
              auth.authorizationHeader
            ).then(() => {
              mutate(['/api/schedules', date, filtered])
              setIsLoading(true)
            })
          }
        }}
        onResize={(event, direction, element, delta, position) => {
          const data = {
            deltaX: 0,
            deltaY: 0,
            node: element,
            ...position,
            direction,
            lastX: 0,
            lastY: 0,
          }
          applyStyleByEventType(data, 'onResize')
        }}
        onResizeStart={(event, direction, element) => {
          const data = {
            deltaX: 0,
            deltaY: 0,
            node: element,
            ...{ x: 0, y: 0 },
            direction,
            lastX: 0,
            lastY: 0,
          }
          applyStyleByEventType(data, 'onResizeStart')
        }}
        onResizeStop={(event, direction, element, delta, position) => {
          const data = {
            deltaX: 0,
            deltaY: 0,
            node: element,
            ...position,
            direction,
            lastX: 0,
            lastY: 0,
          }
          applyStyleByEventType(data, 'onResizeStop')
          const cellCount = delta.height / 5
          const changeMinutes = cellCount * 15

          if (changeMinutes === 0) {
            return
          }

          if (isOverlappedRect(data)) {
            alert(ERROR_DUPLICATE)
            updateKey()
          } else {
            setRndState({
              position: { x: data.x, y: data.y },
              size: { height: rndState.size.height + delta.height, width: rndState.size.width },
            })
          }

          if (direction === 'top') {
            setSchedule(id, subMinutes(start_date, changeMinutes), end_date, column_id, auth.factoryId, auth.authorizationHeader).then(() => {
              mutate(['/api/schedules', date, filtered])
              setIsLoading(true)
            })
          } else if (direction === 'bottom') {
            setSchedule(id, start_date, addMinutes(end_date, changeMinutes), column_id, auth.factoryId, auth.authorizationHeader).then(() => {
              mutate(['/api/schedules', date, filtered])
              setIsLoading(true)
            })
          }
        }}
        data-id={id}
      >
        <ScheduleCell
          isLoading={isLoading}
          data-schedule-cell={true}
          data-id={id}
          color={color}
          onMouseUp={() => {
            handleScheduleMouseUp(id)
          }}
          onMouseDown={() => {
            setIsDrag(false)
          }}
          onMouseMove={() => {
            setIsDrag(true)
          }}
        >
          <Tooltip title={name} style={{ height: '100%' }} enterDelay={2000} placement="bottom-end">
            <div style={{ height: '100%', wordBreak: 'break-all' }}>
              <span>●&nbsp;</span>
              <span>{name}</span>
            </div>
          </Tooltip>
        </ScheduleCell>
      </Rnd>
      <Modal>
        <ModalStyle>
          <ModalTitle>工程計画詳細</ModalTitle>
          <ModalForm onSubmit={handleSubmit(onSubmit)}>
            {listItems}
            {isSubmitDisable ? (
              <LoadingContainer>
                <CircularProgress size={20} />
              </LoadingContainer>
            ) : (
              <ModalSubmit type="submit" disabled={isSubmitDisable} />
            )}
          </ModalForm>
          <ModalClose onClick={close}>CLOSE</ModalClose>
          {(selectedCalendarData.is_ended === 0 && selectedCalendarData.status !== "未計画") && <SubmitPerformances selectedData={selectedCalendarData} type={"button"} parentClose={close} />}
        </ModalStyle>
      </Modal>
    </ScheduleWrapper>
  )
}

export default Schedule

type ScheduleWrapperProps = {
  isLoading: boolean
}

const ScheduleWrapper = styled.div<ScheduleWrapperProps>`
  .resize-handle {
    display: ${({ isLoading }) => (isLoading ? 'none' : 'block')};
  }
`

type ScheduleCellProps = {
  color: Color
  isLoading: boolean
}

const ScheduleCell = styled.div<ScheduleCellProps>`
  cursor: ${({ isLoading }) => (isLoading ? 'wait!important' : 'pointer')};

  padding: 2px 6px;
  font-family: Inter;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0em;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;

  border-left: solid 3px ${({ color }) => color}${({ isLoading }) => (isLoading ? '32' : '')};
  border-radius: 4px;

  width: 100%;
  height: 100%;
  color: ${({ color }) => color}${({ isLoading }) => (isLoading ? (50).toString(16).padStart(2, '0') : '')};
  background-color: ${({ color }) => color}${({ isLoading }) => (isLoading ? (5).toString(16).padStart(2, '0') : (10).toString(16).padStart(2, '0'))}; /* 1Aで10%の透明度の指定 */
`